"use client";

import React, { useState, useRef, useEffect } from "react";
import { useGame } from "@/app/providers/GameContextProvider";
import { motion } from "framer-motion";
import { GameHazard, GameMode } from "@/lib/types/common/GameMode";
import { useIsMobile } from "@/hooks/useIsMobile";
import FinalRankings from "./FinalRankings";
import GameSettings from "./GameSettings";
import RoundRecap from "./RoundRecap";
import { usePhotosphereQuality } from "@/app/providers/PhotosphereQualityProvider";
import { useGameQuality } from '@/app/providers/GameQualityProvider';
import OverlayButtons from "../OverlayButtons";
import { PhotoSphereViewer, PhotoSphereViewerAPI } from "@/app/components/PhotoSphereViewer";
import { saveDailyScore } from "@/lib/utils/dailyUtils";


const GameRecap = () => {
    const { gameState, room } = useGame();
    const { getPhotosphereUrl } = usePhotosphereQuality();
    const { getBackdropBlurIntensity } = useGameQuality();
    const isMobile = useIsMobile();
    const [selectedRoundIndex, setSelectedRoundIndex] = useState<number | null>(null);
    const [hideOverlay, setHideOverlay] = useState<boolean>(false);
    const [hideHazards, setHideHazards] = useState(true);
    const viewerRef = useRef<PhotoSphereViewerAPI | null>(null);
    const roundResult = selectedRoundIndex !== null ? Array.from(gameState!.roundHistory)[selectedRoundIndex] : null;
    const photosphereToShow = roundResult?.photosphere.id || gameState?.roundHistory[0].photosphere.id;
    const hazardsToShow = (roundResult?.hazards || gameState?.roundHistory[0].hazards) as unknown as GameHazard[];

    // Reset hideHazards when round changes
    useEffect(() => {
        setHideHazards(true);
    }, [selectedRoundIndex]);

    // Save daily score when Daily mode game ends
    useEffect(() => {
        if (gameState?.gameMode === GameMode.DAILY && room) {
            // Get the current player's score from finalRankings (preserved) or fall back to live data
            let totalScore = 0;
            const roundScores: number[] = [];
            
            if (gameState.finalRankings && gameState.finalRankings.length > 0) {
                const playerRanking = Array.from(gameState.finalRankings).find(r => r.sessionId === room.sessionId);
                if (playerRanking) {
                    totalScore = playerRanking.totalScore;
                    
                    // Extract round scores from roundHistory
                    gameState.roundHistory.forEach((round) => {
                        const playerResult = round.playerResults.get(room.sessionId);
                        if (playerResult && playerResult.scores && playerResult.scores.length >= round.roundNumber) {
                            roundScores.push(playerResult.scores[round.roundNumber - 1]);
                        }
                    });
                }
            } else {
                const currentPlayer = gameState.players?.get(room.sessionId);
                if (currentPlayer) {
                    totalScore = currentPlayer.totalScore;
                    
                    // Extract round scores from roundHistory
                    gameState.roundHistory.forEach((round) => {
                        const playerResult = round.playerResults.get(room.sessionId);
                        if (playerResult && playerResult.scores && playerResult.scores.length >= round.roundNumber) {
                            roundScores.push(playerResult.scores[round.roundNumber - 1]);
                        }
                    });
                }
            }
            if (totalScore > 0) {
                saveDailyScore(totalScore, roundScores);
            }
        }
    }, [gameState?.gameMode, gameState?.finalRankings, gameState?.players, room]);

    // Update autorotate when hideOverlay changes
    useEffect(() => {
        if (viewerRef.current) {
            if (hideOverlay) {
                viewerRef.current.stopAutoRotate();
            } else {
                viewerRef.current.startAutoRotate();
            }
        }
    }, [hideOverlay, selectedRoundIndex]);



    if (!gameState || !room) return null;

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative w-full h-full anti-flashbang overflow-auto flex items-center justify-center"
            >
                <div className="fixed w-full h-full">
                    <PhotoSphereViewer
                        ref={viewerRef}
                        src={getPhotosphereUrl(photosphereToShow || "")}
                        height="100%"
                        width="100%"
                        defaultYaw={-Math.PI/2}
                        hazards={hazardsToShow || []}
                        showHazards={!hideHazards}
                        autoRotate={!hideOverlay}
                        autoRotateSpeed={0.02}
                        shader={hazardsToShow?.includes(GameHazard.PIXELATE) ? 'pixelate' : null}
                        shaderSettings={{ pixelSize: 8 }}
                        onReady={() => {
                            if (!hideOverlay) {
                                viewerRef.current?.startAutoRotate();
                            }
                        }}
                    />
                    {!hideOverlay && <div className={`w-full h-full absolute top-0 left-0 bg-gradient-to-t from-[#030b18]/60 to-[#030b18]/10 ${getBackdropBlurIntensity('md')} z-[2]`}></div>}
                </div>
                <OverlayButtons
                    hideHazards={hideHazards}
                    setHideHazards={setHideHazards}
                    showResultOverlay={!hideOverlay}
                    setShowResultOverlay={() => setHideOverlay(false)}
                />
                <motion.div
                    initial={{ opacity: 1 }}
                    animate={{ opacity: hideOverlay ? 0 : 1 }}
                    transition={{ duration: 0.3 }}
                    className={`relative max-h-[80vh] 2xl:max-h-[90vh] overflow-y-auto w-full flex flex-col items-center gap-4 md:gap-10 z-[3] px-2 md:px-0 py-4 md:py-0 ${hideOverlay ? 'no-pointer-events' : ''}`}
                >
                    <div className="flex flex-col lg:flex-row gap-4 md:gap-10 justify-center items-center w-full max-w-[95vw] lg:max-w-none">
                        <FinalRankings isMobile={isMobile} />
                        <GameSettings isMobile={isMobile} />
                    </div>
                    <RoundRecap
                        selectedRoundIndex={selectedRoundIndex}
                        setSelectedRoundIndex={setSelectedRoundIndex}
                        setHideOverlay={setHideOverlay}
                    />
                </motion.div>
            </motion.div>
        </>
    );
};

export default GameRecap;